import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, User, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAgentAuth } from '../../contexts/AgentAuthContext';
import { TOOL_IDS } from '../../lib/agents/firestore-schema';

const TOOL_NAV = [
  { id: TOOL_IDS.PLACEHOLDER, path: 'placeholder', label: 'Placeholder Tool' },
  { id: TOOL_IDS.FINANCIAL_NEED_ANALYSIS, path: 'financial-need-analysis', label: 'Financial Need Analysis' },
  { id: TOOL_IDS.INVESTMENT_COMPARISON, path: 'investment-comparison', label: 'Investment Comparison' },
  { id: TOOL_IDS.IMO_COMPENSATION, path: 'imo-compensation', label: 'IMO Compensation & Promotion' },
];

export default function AgentsLayout() {
  const { agentProfile, signOut } = useAgentAuth();
  const navigate = useNavigate();

  const handleSignOut = () => {
    signOut();
    navigate('/agents');
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="flex justify-between items-center h-14 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto w-full">
          <div className="flex items-center gap-5">
            <NavLink to="/" className="flex items-center hover:opacity-85 transition-opacity" aria-label="Grow And Stay Rich">
              {/* Two-part brand lockup: navy pill + Playfair Display wordmark */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44 }}>
                {/* Navy pill — mix-blend-mode:screen strips black bg, gold mark glows */}
                <div style={{
                  width: 38, height: 38,
                  background: '#0D1B2A',
                  borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}>
                  <img
                    src="/images/logo/logomark-white.svg"
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                    style={{ width: '70%', height: '70%', objectFit: 'contain', display: 'block' }}
                  />
                </div>
                {/* Brand wordmark — Playfair Display, brand gold, two lines */}
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                  color: '#C9A84C',
                  lineHeight: 1.18,
                  letterSpacing: '0.01em',
                  userSelect: 'none',
                }}>
                  <div style={{ fontSize: 14 }}>Grow And</div>
                  <div style={{ fontSize: 14 }}>Stay Rich</div>
                </div>
              </div>
            </NavLink>
            <span className="text-border" aria-hidden>|</span>
            <NavLink
              to="/agents"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Agent Tools
            </NavLink>
          </div>
          <div className="flex items-center gap-3">
            {agentProfile && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground truncate max-w-[180px] sm:max-w-none">
                {agentProfile.photoURL ? (
                  <img
                    src={agentProfile.photoURL}
                    alt=""
                    className="w-7 h-7 rounded-full shrink-0"
                  />
                ) : (
                  <User className="w-5 h-5 shrink-0" />
                )}
                <span className="truncate">{agentProfile.displayName || agentProfile.email}</span>
              </span>
            )}
            <Button variant="outline" size="sm" onClick={handleSignOut} className="shrink-0">
              <LogOut className="w-4 h-4 mr-1" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 gap-8 lg:gap-10">
        <aside className="w-52 lg:w-56 shrink-0">
          <nav className="space-y-6 sticky top-[3.5rem]">
            <div>
              <NavLink
                to="/agents"
                end
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`
                }
              >
                <LayoutDashboard className="w-4 h-4 shrink-0" />
                Dashboard
              </NavLink>
            </div>
            <div>
              <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Tools
              </p>
              <ul className="space-y-0.5">
                {TOOL_NAV.map(({ path, label }) => (
                  <li key={path}>
                    <NavLink
                      to={`/agents/${path}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`
                      }
                    >
                      <Wrench className="w-4 h-4 shrink-0 opacity-70" />
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
